import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { apiCall } from '../functions/common';

const TranslationsContext = createContext(null);

const LANG_CODE_TO_ID = { nl: 1, en: 2 };
const LANG_ID_TO_CODE = { 1: 'nl', 2: 'en' };

const TRANSLATIONS_VERSION = 'v1';
const storageKey = (langId) => `translations_${TRANSLATIONS_VERSION}_lang_${langId}`;
const storageLangKey = 'lang_id';

export function TranslationsProvider({ children }) {
    const code = global?.settings?.api?.code;

    // start met localStorage taal, anders NL=1
    const initialLangId = (() => {
        const saved = parseInt(localStorage.getItem(storageLangKey), 10);
        return Number.isInteger(saved) ? saved : 1;
    })();

    const [langId, setLangId] = useState(initialLangId);
    const [translations, setTranslations] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    //console.log(translations);
    // Hydrate uit cache (per taal)
    useEffect(() => {
        const cached = localStorage.getItem(storageKey(langId));
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                if (parsed && parsed.data && typeof parsed.data === 'object') {
                    setTranslations(parsed.data);
                    setLoading(false);
                    return; // we tonen direct cache; fetch doet daarna een “silent refresh”
                }
            } catch (_) {}
        }
        setTranslations({});
        setLoading(true);
    }, [langId]);

    // Fetch vertalingen bij (code/langId)-wijziging
    useEffect(() => {
        let cancelled = false;

        async function fetchTranslations() {
            try {
                setError(null);
                const resp = await apiCall(`algemeen?code=${code}`);
                if (cancelled) return;

                if (resp?.data?.success) {
                    const teksten = resp.data.data?.teksten || {};
                    setTranslations(teksten);
                    localStorage.setItem(
                        storageKey(langId),
                        JSON.stringify({ savedAt: moment().toISOString(), data: teksten })
                    );
                }
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        if (code && langId) fetchTranslations();
        return () => { cancelled = true; };
    }, [code, langId]);

    // t() helper
    const t = useMemo(() => {
        return (key, fallback = '') => {
            if (!key) return fallback;
            if (key.includes('.')) {
                return key
                    .split('.')
                    .reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), translations) ?? fallback;
            }
            return translations[key] ?? fallback;
        };
    }, [translations]);

    // taal wisselen; accepteert 'nl' | 'en' of een langId (1/2)
    const setLanguage = (lang) => {
        const newLangId = typeof lang === 'string' ? (LANG_CODE_TO_ID[lang] || 1) : lang;
        localStorage.setItem(storageLangKey, newLangId);
        setLangId(newLangId);
    };

    const languageCode = LANG_ID_TO_CODE[langId] || 'nl';

    const value = useMemo(
        () => ({ t, translations, loading, error, langId, languageCode, setLanguage }),
        [t, translations, loading, error, langId, languageCode]
    );

    return (
        <TranslationsContext.Provider value={value}>
            {children}
        </TranslationsContext.Provider>
    );
}

export function useTranslations() {
    const ctx = useContext(TranslationsContext);
    if (!ctx) throw new Error('useTranslations must be used within a TranslationsProvider');
    return ctx;
}
